import PropTypes from 'prop-types';
/* eslint-disable */

import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Avatar,
    IconButton,
    Grid,
    Paper,
    Divider,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
    Link,
    Stack,
    CircularProgress,
    ButtonBase
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { viewEmployee } from 'utils/API/Mis Apis/HrPortelApis';
import {
    Close,
    Email,
    Phone,
    LocationOn,
    CalendarToday,
    AssignmentInd,
    VerifiedUser,
    School,
    Badge,
    AccountBalance,
    Description,
    Public,
    Work,
    HistoryEdu,
    FileDownload,
    Visibility
} from '@mui/icons-material';
import moment from 'moment';
import { motion } from 'framer-motion';
import { getRoleColor } from '../utils/dashboardUtils';
import { BgvChip, TrainingChip } from './StatusChips';

const InfoItem = ({ icon: Icon, label, value, isLink, href, color = '#64748b' }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.65rem'
            }}
        >
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: `${color}08`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: `${color}15`
                }}
            >
                <Icon sx={{ color: color, fontSize: 18 }} />
            </Box>
            {isLink && value ? (
                <Link
                    href={
                        href ||
                        (typeof value === 'string' && value.startsWith('http') ? value : `${process.env.REACT_APP_API_IMAGE_URL}/${value}`)
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        wordBreak: 'break-all'
                    }}
                >
                    View Document
                </Link>
            ) : (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                    {value || 'NA'}
                </Typography>
            )}
        </Box>
    </Box>
);

const ImageButton = styled(ButtonBase)(({ theme }) => ({
    position: 'relative',
    height: 140,
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        height: 100
    },
    '&:hover, &.Mui-focusVisible': {
        zIndex: 1,
        '& .MuiImageBackdrop-root': {
            opacity: 0.15
        },
        '& .MuiImageMarked-root': {
            opacity: 0
        },
        '& .MuiTypography-root': {
            border: '4px solid currentColor'
        }
    }
}));

const ImageSrc = styled('span')({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center 40%'
});

const ImageArea = styled('span')(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white
}));

const ImageBackdrop = styled('span')(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.common.black,
    opacity: 0.4,
    transition: theme.transitions.create('opacity')
}));

const ImageMarked = styled('span')(({ theme }) => ({
    height: 3,
    width: 18,
    backgroundColor: theme.palette.common.white,
    position: 'absolute',
    bottom: -2,
    left: 'calc(50% - 9px)',
    transition: theme.transitions.create('opacity')
}));

const DocumentCard = ({ label, value, icon: Icon, color, getFileUrl }) => {
    const url = getFileUrl(value);
    const hasValue = !!value;

    const isPdf = hasValue && value.toLowerCase().endsWith('.pdf');
    const bgUrl = hasValue ? (isPdf ? 'https://www.adobe.com/content/dam/cc/icons/pdficon.png' : url) : '';

    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                opacity: hasValue ? 1 : 0.7,
                '&:hover': hasValue
                    ? {
                          borderColor: color,
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      }
                    : {}
            }}
        >
            <ImageButton
                focusRipple
                disabled={!hasValue}
                component={hasValue ? 'a' : 'div'}
                href={hasValue ? url : undefined}
                target={hasValue ? '_blank' : undefined}
                rel={hasValue ? 'noopener noreferrer' : undefined}
                sx={{
                    bgcolor: !hasValue ? '#f1f5f9' : undefined
                }}
            >
                {hasValue ? (
                    <>
                        <ImageSrc style={{ backgroundImage: `url(${bgUrl})` }} />
                        <ImageBackdrop className="MuiImageBackdrop-root" />
                        <ImageArea>
                            <Typography
                                component="span"
                                variant="subtitle2"
                                color="inherit"
                                sx={(theme) => ({
                                    position: 'relative',
                                    p: 2,
                                    pt: 1,
                                    pb: `calc(${theme.spacing(1)} + 2px)`,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                    letterSpacing: '0.05em'
                                })}
                            >
                                Preview
                                <ImageMarked className="MuiImageMarked-root" />
                            </Typography>
                        </ImageArea>
                    </>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.disabled' }}>
                        <Typography variant="overline" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            No Image Found
                        </Typography>
                    </Box>
                )}
            </ImageButton>

            <Box sx={{ mt: 2, px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: hasValue ? `${color}10` : 'rgba(0,0,0,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Icon sx={{ color: hasValue ? color : 'text.disabled', fontSize: 14 }} />
                    </Box>
                    <Typography
                        variant="caption"
                        fontWeight={800}
                        color={hasValue ? 'text.primary' : 'text.disabled'}
                        sx={{
                            textTransform: 'uppercase',
                            fontSize: '0.6rem',
                            letterSpacing: '0.02em',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {label}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        fullWidth
                        disabled={!hasValue}
                        variant="contained"
                        size="small"
                        component={hasValue ? 'a' : 'button'}
                        href={hasValue ? url : undefined}
                        download={hasValue}
                        startIcon={<FileDownload sx={{ fontSize: '14px !important' }} />}
                        sx={{
                            borderRadius: '8px',
                            fontSize: '0.65rem',
                            py: 0.6,
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: color,
                            '&:hover': { bgcolor: color, opacity: 0.9, boxShadow: `0 4px 10px ${color}30` },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(0,0,0,0.05)',
                                color: 'text.disabled'
                            }
                        }}
                    >
                        Download
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
};

const FlippingAvatar = ({ src, name, color }) => {
    const [isFlipped, setIsFlipped] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setIsFlipped((prev) => !prev);
        }, 4000); // Flip every 4 seconds
        return () => clearInterval(interval);
    }, []);

    return (
        <Box
            sx={{
                perspective: '1000px',
                width: 72,
                height: 72,
                cursor: 'pointer'
            }}
            onMouseEnter={() => setIsFlipped(true)}
            onMouseLeave={() => setIsFlipped(false)}
        >
            <motion.div
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.7, type: 'spring', stiffness: 140, damping: 15 }}
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    transformStyle: 'preserve-3d'
                }}
            >
                {/* Front Side: Photo */}
                <Avatar
                    src={src}
                    sx={{
                        width: 72,
                        height: 72,
                        position: 'absolute',
                        backfaceVisibility: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                        border: '3px solid rgba(255,255,255,0.3)',
                        '& img': { objectFit: 'cover' }
                    }}
                >
                    {name?.charAt(0)}
                </Avatar>
                {/* Back Side: Letter */}
                <Avatar
                    sx={{
                        width: 72,
                        height: 72,
                        position: 'absolute',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        bgcolor: '#fff',
                        color: color,
                        fontSize: 32,
                        fontWeight: 900,
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                        border: '3px solid rgba(255,255,255,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {name?.charAt(0)}
                </Avatar>
            </motion.div>
        </Box>
    );
};

const SectionContainer = ({ title, icon: Icon, children, color }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            height: '100%',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255, 255, 255, 0.5)',
            transition: 'all 0.3s ease',
            '&:hover': {
                borderColor: color || 'primary.main',
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
            }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
            <Icon sx={{ color: color || 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
            </Typography>
        </Box>
        <Grid container spacing={1}>
            {children}
        </Grid>
    </Paper>
);

const EmployeeDetailsModal = ({ open, handleClose, employee }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [detailedEmployee, setDetailedEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastEmployee, setLastEmployee] = useState(null);

    // Save current employee into lastEmployee to keep data during closing animation
    useEffect(() => {
        if (employee) {
            setLastEmployee(employee);
        }
    }, [employee]);

    // Helper to handle full S3 URLs from API or relative paths
    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_IMAGE_URL}/${path}`;
    };

    useEffect(() => {
        const fetchDetails = async () => {
            if (open && employee?.id) {
                setLoading(true);
                try {
                    const response = await viewEmployee({ employee_id: employee.id });
                    if (response?.data?.success) {
                        setDetailedEmployee(response.data.data);
                    }
                } catch (error) {
                    console.error('Error fetching employee details:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchDetails();
        // Don't clear detailedEmployee immediately on close to allow animation to wrap up
        // It will be cleared/overwritten when next opened or by common reset if needed
    }, [open, employee?.id]);

    const displayData = detailedEmployee || employee || lastEmployee;
    const roleColor = displayData ? getRoleColor(displayData.role) : '#1976d2';

    if (!displayData) return null;

    const handleEdit = () => {
        handleClose();
        navigate('/HR/add-sales-employee', { state: { editEmployeeData: employee } });
    };

    const isDeactivated = displayData.status !== 'Active';

    // Helper for formatting dates
    const formatDate = (date) => (date ? moment(date).format('DD MMM YYYY') : 'NA');

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 6,
                    overflow: 'hidden',
                    maxHeight: '90vh',
                    bgcolor: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(8px)'
                    }
                }
            }}
        >
            {/* Header Section (Reverted to Old Style) */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 60%, #42a5f5 100%)',
                    px: 3,
                    py: 4,
                    position: 'relative',
                    color: '#fff',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {/* Decorative circles */}
                {[
                    { size: 120, top: -40, right: -20, op: 0.15 },
                    { size: 80, bottom: -20, right: 40, op: 0.1 }
                ].map((c, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: c.top,
                            bottom: c.bottom,
                            right: c.right,
                            width: c.size,
                            height: c.size,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            opacity: c.op,
                            pointerEvents: 'none'
                        }}
                    />
                ))}

                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'rgba(255,255,255,0.7)',
                        zIndex: 2,
                        '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
                        <FlippingAvatar src={getFileUrl(displayData.profile)} name={displayData.name} color={roleColor} />
                    </motion.div>

                    <Box>
                        <motion.div
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.4 }}
                        >
                            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.01em', color: '#fff', mb: 0.5 }}>
                                {displayData.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#fff',
                                        opacity: 0.9,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.65rem',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1
                                    }}
                                >
                                    {displayData.role?.replace('_', ' ')}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.65rem', color: '#fff' }}>
                                    •
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.02em', color: '#fff' }}
                                >
                                    ID: {displayData.employee_id || displayData.id}
                                </Typography>
                                {displayData.zone && (
                                    <>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.6, fontWeight: 600, fontSize: '0.65rem', color: '#fff' }}
                                        >
                                            •
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.8, fontWeight: 700, fontSize: '0.65rem', color: '#fff' }}
                                        >
                                            Zone: {displayData.zone}
                                        </Typography>
                                    </>
                                )}
                                {loading && <CircularProgress size={16} sx={{ color: '#fff', ml: 1, opacity: 0.7 }} />}
                            </Box>
                        </motion.div>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 3, bgcolor: 'transparent' }}>
                <Grid container spacing={3}>
                    {/* Status Highlights */}
                    <Grid item xs={12}>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.4 }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={800}
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1, letterSpacing: '0.05em' }}
                                        >
                                            EMPLOYMENT STATUS
                                        </Typography>
                                        <Chip
                                            label={displayData.status || 'Active'}
                                            size="small"
                                            color={isDeactivated ? 'error' : 'success'}
                                            sx={{ fontWeight: 800, fontSize: '0.7rem', borderRadius: 1.5, height: 24 }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={800}
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1, letterSpacing: '0.05em' }}
                                        >
                                            BGV VERIFICATION
                                        </Typography>
                                        <BgvChip v={displayData.bgv_status} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: '1px solid rgba(255, 255, 255, 0.7)',
                                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={800}
                                            color="text.secondary"
                                            sx={{ display: 'block', mb: 1, letterSpacing: '0.05em' }}
                                        >
                                            TRAINING STATUS
                                        </Typography>
                                        <TrainingChip v={displayData.training_status} />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </motion.div>
                    </Grid>

                    {/* Section: Personal Info */}
                    <Grid item xs={12} md={4}>
                        <SectionContainer title="Personal Details" icon={AssignmentInd} color={roleColor}>
                            <Grid item xs={12}>
                                <InfoItem icon={Email} label="Email Address" value={displayData.email} />
                                <InfoItem icon={Phone} label="Mobile Number" value={displayData.phone} />
                                <InfoItem icon={Badge} label="PAN Card" value={displayData.pan} />
                                <InfoItem icon={Badge} label="Aadhaar Number" value={displayData.aadhaar_number} />
                            </Grid>
                        </SectionContainer>
                    </Grid>

                    {/* Section: Work Details */}
                    <Grid item xs={12} md={4}>
                        <SectionContainer title="Work Information" icon={Work} color={roleColor}>
                            <Grid item xs={12}>
                                <InfoItem
                                    icon={Work}
                                    label="Designation"
                                    value={displayData.designation_name || displayData.designation || 'NA'}
                                />
                                <InfoItem icon={Public} label="Region (Zone)" value={displayData.zone} />
                                <InfoItem icon={CalendarToday} label="Offer Date" value={formatDate(displayData.date_of_offer)} />
                                <InfoItem icon={CalendarToday} label="Joining Date" value={formatDate(displayData.date_of_joining)} />
                                <InfoItem
                                    icon={HistoryEdu}
                                    label="Effective Date"
                                    value={formatDate(displayData.effective_employment_date)}
                                />
                            </Grid>
                        </SectionContainer>
                    </Grid>

                    {/* Section: Location & Lead */}
                    <Grid item xs={12} md={4}>
                        <SectionContainer title="Location & Lead" icon={LocationOn} color={roleColor}>
                            <Grid item xs={12}>
                                <InfoItem icon={LocationOn} label="State" value={displayData.state_name || displayData.stateList} />
                                <InfoItem
                                    icon={LocationOn}
                                    label="District"
                                    value={displayData.district_name || displayData.districtList}
                                />
                                <InfoItem icon={LocationOn} label="City" value={displayData.city_name || displayData.cityList} />
                                <InfoItem icon={PersonOutlined} label="Reporting Manager" value={displayData.reporting_manager_name} />
                            </Grid>
                        </SectionContainer>
                    </Grid>

                    {/* Section: Bank Details */}
                    <Grid item xs={12} md={6}>
                        <SectionContainer title="Bank Information" icon={AccountBalance} color={roleColor}>
                            <Grid item xs={12} sm={6}>
                                <InfoItem icon={AccountBalance} label="Bank Name" value={displayData.bank_name} />
                                <InfoItem icon={AssignmentInd} label="Account Number" value={displayData.bank_account_number} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InfoItem icon={Badge} label="IFSC Code" value={displayData.bank_ifsc_code} />
                            </Grid>
                        </SectionContainer>
                    </Grid>

                    {/* BGV Remarks */}
                    <Grid item xs={12} md={6}>
                        <SectionContainer title="BGV Remarks" icon={VerifiedUser} color={roleColor}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                                    {displayData.bgv_remarks || 'No remarks provided.'}
                                </Typography>
                            </Grid>
                        </SectionContainer>
                    </Grid>

                    {/* Section: Documents */}
                    <Grid item xs={12}>
                        <SectionContainer title="Employee Documents" icon={Description} color={roleColor}>
                            {[
                                { label: 'Profile Photo', key: 'profile' },
                                { label: 'Aadhar Card', key: 'aadhar' },
                                { label: 'PAN Card', key: 'poa' },
                                { label: 'Offer Letter', key: 'offer_letter' },
                                { label: 'Employment Agreement', key: 'employment_agreement' },
                                { label: 'NDA', key: 'nda' },
                                { label: 'Proforma', key: 'proforma' },
                                { label: 'Bank Document', key: 'bank_account_document' },
                                { label: 'EDU Cert.', key: 'educational_certificate' },
                                { label: 'Exp. Letter', key: 'experience_letter' },
                                { label: 'Salary Slip', key: 'salary_slip' },
                                { label: 'CV', key: 'cv' },
                                { label: 'CIF', key: 'cif' },
                                { label: 'BGV Doc', key: 'bgv_document' }
                            ].map((doc) => (
                                <Grid item xs={12} sm={6} md={3} lg={2.4} key={doc.key}>
                                    <DocumentCard
                                        label={doc.label}
                                        value={displayData[doc.key]}
                                        icon={Description}
                                        color={roleColor}
                                        getFileUrl={getFileUrl}
                                    />
                                </Grid>
                            ))}
                        </SectionContainer>
                    </Grid>
                </Grid>
            </DialogContent>

            <Box
                sx={{
                    p: 2.5,
                    px: 3,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    borderTop: '1px solid rgba(255, 255, 255, 0.8)',
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 3, textTransform: 'none', px: 4, fontWeight: 700, borderColor: 'rgba(0,0,0,0.2)' }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleEdit}
                    disabled={isDeactivated}
                    startIcon={<Badge />}
                    sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        px: 4,
                        fontWeight: 700,
                        bgcolor: roleColor,
                        boxShadow: isDeactivated ? 'none' : `0 8px 20px ${roleColor}40`,
                        '&:hover': { bgcolor: roleColor, opacity: 0.9, transform: 'translateY(-1px)' },
                        transition: 'all 0.2s'
                    }}
                >
                    Edit Profile
                </Button>
            </Box>
        </Dialog>
    );
};

// Mock PersonOutlined icon since it's used
const PersonOutlined = Badge;

EmployeeDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object
};

export default EmployeeDetailsModal;
